// Comparison and "alternative to" pages, keyed by route slug (no leading "/").
//
// Why this is data and not JSX: scripts/prerender.cjs writes crawlable body
// HTML only for routes whose content lives in a file it can read at build time
// (src/content/docs.js, src/content/legal.js, src/data/pages.js and this one).
// A hand-built JSX page gets head tags and an empty <div id="root">, which is
// the wrong trade for pages whose entire job is to rank for a search someone
// types when they are already shopping. Keep the words here.
//
// Content rules these pages are written to, from OwnPBX_Marketing_Brief.md:
//
//   - Nothing is asserted about another company's current product. We cannot
//     track what anyone ships this quarter, and a comparison table that goes
//     stale is worse than no table. So the middle column of every table is a
//     question to put to them, not a claim about them. Only the OwnPBX column
//     states facts, and every one of those has to be true today.
//   - No competitor is named insultingly. Where a category genuinely suits a
//     buyer better, the page says so — "where they are a better fit" is on
//     every page and is not decoration.
//   - No customers, testimonials, user counts, uptime figures or certifications.
//     We have none. "Built for", never "trusted by".
//   - No prices. The tiers come from the API at runtime; a number written into
//     a page here would contradict the pricing page within a quarter.
//   - Nothing promised that is not live: no WhatsApp, no mobile app, no
//     self-service signup. Each page says so out loud in its "limits" block,
//     because a buyer who finds that out on the call is a buyer we wasted.
//
// Shape, all optional except title and lead:
//   category  kicker above the h1
//   title     the h1
//   lead      one sentence under it
//   intro     [paragraph]
//   note      a single aside, rendered with the blue rule
//   different { h, lead, items: [{ title, desc }] }
//   table     { h, lead, head: [], rows: [[]] }
//   limits    { h, lead, list: [] }
//   betterFit { h, lead, list: [] }
//   faqs      [{ q, a }]
//   related   [slug]

const NO_CUSTOMERS =
  "OwnPBX is a new product from Oneway Networks Technology Pvt. Ltd. We have no " +
  "customer logos, case studies or uptime figures to show you, and we would rather " +
  "say that here than have you find out on the call.";

const STANDARD_LIMITS = [
  "No WhatsApp or bulk SMS. The AI agent works on phone calls and on web chat, and that is all.",
  "No mobile app. Your team uses a desk phone, a softphone on a laptop, or the browser phone with nothing to install.",
  "No self-service signup. We set the account up with you, so you cannot swipe a card at midnight and be live by morning.",
  "No published uptime number or compliance certification. We will tell you exactly what the infrastructure is and where it runs; we will not quote a figure we cannot evidence.",
];

export const compare = {
  // --- alternative-to pages ------------------------------------------------

  "compare/knowlarity-alternative": {
    category: "Alternatives",
    title: "Looking for a Knowlarity alternative?",
    lead:
      "OwnPBX is a cloud phone system with AI voice agents built into it, sold as software — you keep the carrier and the numbers you already have, or buy calling from us if you would rather not.",
    intro: [
      "Knowlarity is one of the long-established cloud telephony providers in India, and plenty of businesses are served perfectly well by that kind of product. This page is here to help you decide, not to talk you out of them.",
      "We do not track what any other provider ships from one quarter to the next, so nothing on this page claims what Knowlarity does today — check their own pricing and feature pages for that. What follows is what OwnPBX does, stated plainly, and the questions worth putting to every provider on your shortlist, us included.",
    ],
    note: NO_CUSTOMERS,
    different: {
      h: "What OwnPBX does differently",
      lead: "Four things, and they are the reason the product exists.",
      items: [
        {
          title: "Your carrier stays yours",
          desc:
            "Point the SIP trunk and the numbers you already have at OwnPBX and pay for the software only. The telecom contract you negotiated stays in your name. If you would rather buy calling from us, that works too — it is a choice, not a condition of using the platform.",
        },
        {
          title: "The AI agent lives inside the phone system",
          desc:
            "An AI agent is a destination a number can ring, exactly like an extension, an IVR option or a queue overflow. Because it is inside the switch rather than bolted on beside it, the agent can pass a live caller to a real extension mid-call, with the context of what has already been said.",
        },
        {
          title: "Hindi and Hinglish, not English with an Indian accent",
          desc:
            "The agents are built on Indian speech models, so Indian names, spoken numbers and code-switching in the middle of a sentence are handled the way callers actually talk. Ten-plus Indian languages are covered, and the agent follows the caller when they switch.",
        },
        {
          title: "Per-call visibility, including what the call cost",
          desc:
            "Every call's journey appears in the console step by step — which number it came in on, what it rang, how long it waited, what it cost. Not a report that lands the following month as a PDF.",
        },
      ],
    },
    table: {
      h: "Six questions worth asking both of us",
      lead:
        "The middle column is deliberately a question rather than a claim about Knowlarity: ask them, and compare their answer with ours.",
      head: ["What to compare", "Ask any provider", "How OwnPBX works"],
      rows: [
        [
          "Calling minutes",
          "Can I keep my own carrier and numbers and pay for the software alone?",
          "Yes. Bring your own SIP trunk, or buy calling from us — both are supported, and you can change your mind later.",
        ],
        [
          "AI voice agents",
          "Is the AI a separate product on its own contract, or part of the phone system?",
          "Part of the same platform. Any number, IVR option or queue can ring an agent, and the agent can transfer to a human mid-call with context.",
        ],
        [
          "Languages",
          "Does the voice bot handle Hindi, Hinglish and Indian names, or is it English-first?",
          "English, Hindi and natural Hinglish, plus ten-plus Indian languages, with mid-conversation switching.",
        ],
        [
          "Reporting",
          "How soon after a call can I see it, and does it show what that call cost?",
          "In the console per call, with the cost of the call, alongside live wallboards for supervisors.",
        ],
        [
          "Multi-tenant",
          "Can one account hold several branches or clients with separate users, numbers and reports?",
          "Yes — the platform is multi-tenant, and resellers can run their own customers on it.",
        ],
        [
          "Getting started",
          "Who configures it, and how long until the first call?",
          "We build it with you. Extensions, IVRs and queues can be done the same day; moving numbers takes as long as your carrier's process.",
        ],
      ],
    },
    limits: {
      h: "What OwnPBX does not do",
      lead: "In case one of these is the thing you need.",
      list: STANDARD_LIMITS.concat([
        "No long reference list. If procurement needs a vendor with years of audited history, we are not that yet.",
      ]),
    },
    betterFit: {
      h: "Where an established provider is the better choice",
      lead:
        "There are buyers we are plainly wrong for, and it is cheaper for both of us to find that out now.",
      list: [
        "You want one vendor for the telecom and the software, and you would rather never think about a carrier contract at all.",
        "Your procurement process needs a supplier with a long client list, formal audits and a large support organisation behind it.",
        "You need a channel we do not have — WhatsApp, bulk SMS campaigns, or a native mobile app for a field team.",
        "Your call volume is small enough that a bundled plan is simply less work than managing a trunk.",
      ],
    },
    faqs: [
      {
        q: "Can I keep my existing phone numbers?",
        a: "Yes, as long as your carrier will point them at our SIP trunk, which is a normal request. If you would rather we supplied the numbers, we can give you city-code numbers across the Indian metros.",
      },
      {
        q: "Is the AI agent an expensive add-on?",
        a: "It is part of the same platform, but it is priced separately from PBX extensions because not every customer wants it. The current plans are on the pricing page — they come from the billing system, so that page is always the accurate one.",
      },
      {
        q: "Does the agent actually handle Hinglish, or just Hindi?",
        a: "Hinglish, including switching language mid-sentence, Indian names and numbers spoken the Indian way. You can listen to agents answering live calls on the AI demos page without booking anything.",
      },
      {
        q: "How long does switching take?",
        a: "The phone system side is quick — extensions, IVRs, queues and agents can be configured in a day. The timeline is really set by your carrier and how fast they repoint numbers.",
      },
    ],
    related: [
      "compare/exotel-alternative",
      "compare/myoperator-alternative",
      "compare/bring-your-own-carrier-cloud-pbx",
    ],
  },

  "compare/exotel-alternative": {
    category: "Alternatives",
    title: "Looking for an Exotel alternative?",
    lead:
      "OwnPBX is a phone system your team logs into, with AI voice agents inside it — not a set of calling APIs you assemble into a phone system yourself.",
    intro: [
      "Exotel sits in the cloud communications platform category: APIs and building blocks that developers wire into their own product. That is a different shape of thing from what OwnPBX sells, and the difference matters more than any feature-by-feature table.",
      "This page does not describe Exotel's current product — we have no way to keep that accurate, so we do not try. It describes the two categories honestly, says what OwnPBX does, and is blunt about the work a platform does better.",
    ],
    note: NO_CUSTOMERS,
    different: {
      h: "A phone system first, an API second",
      lead: "OwnPBX has an API and webhooks. It is not primarily an API.",
      items: [
        {
          title: "Your staff get a console, not an SDK",
          desc:
            "Extensions, desk phones, a browser phone, IVR menus, queues, business hours, voicemail, recording and wallboards are configured by an operations person in a browser. Nobody has to ship code to change a call flow at five o'clock.",
        },
        {
          title: "The AI agent is a routing destination",
          desc:
            "You do not glue a bot to a call leg. An AI agent is something a number can ring, the same way it can ring a queue — and it hands the caller to a human extension mid-call with the conversation so far intact.",
        },
        {
          title: "Bring your own carrier",
          desc:
            "Keep your existing SIP trunk, numbers and negotiated rates and pay us for software. Buying calling from us is an option, not a requirement.",
        },
        {
          title: "An API for the things that deserve one",
          desc:
            "A REST API and webhooks cover extensions, numbers, call records and AI agent events, so the call outcome and transcript can land in your CRM the moment a call ends. It is the integration surface of a phone system, not a communications platform.",
        },
      ],
    },
    table: {
      h: "Deciding between a platform and a phone system",
      lead:
        "The middle column is a question to put to any CPaaS you are evaluating, not a statement about one.",
      head: ["What to compare", "Ask any provider", "How OwnPBX works"],
      rows: [
        [
          "Who operates it day to day",
          "Does changing a call flow need a developer, or can an operations lead do it?",
          "An operations lead, in the console. No deployment involved.",
        ],
        [
          "AI voice agents",
          "Is the voice bot a component I integrate, or a destination the phone system already knows about?",
          "A destination. Point a number, an IVR key or a queue overflow at an agent and it answers.",
        ],
        [
          "Minutes",
          "Am I required to buy calling through the platform?",
          "No. Bring your own carrier, or buy calling from us.",
        ],
        [
          "Languages",
          "How does the voice bot handle Hindi and Hinglish code-switching?",
          "Built on Indian speech models — Hindi, Hinglish and ten-plus Indian languages, switching as the caller does.",
        ],
        [
          "Integration",
          "What arrives at my CRM when a call ends, and how soon?",
          "A webhook on completion with the outcome, transcript, sentiment and the call's cost.",
        ],
        [
          "Omnichannel",
          "Do I need SMS, WhatsApp and verification flows in the same place?",
          "We do not offer those. Voice calls and web chat only — if you need the rest, a platform is the right category.",
        ],
      ],
    },
    limits: {
      h: "What OwnPBX does not do",
      lead: "This list is longer than a sales page would normally admit to. It is accurate.",
      list: STANDARD_LIMITS.concat([
        "No SMS, OTP or verification APIs, and no omnichannel messaging. The API covers the phone system and the AI agents.",
        "No global number inventory. The numbers we supply are Indian city-code numbers.",
      ]),
    },
    betterFit: {
      h: "Where a communications platform is the better fit",
      lead: "If any of these describe you, buy the platform — you will be happier.",
      list: [
        "Calling is a feature inside your own product, and your developers want primitives rather than a console.",
        "You need SMS, WhatsApp, OTP verification and voice behind one set of APIs and one bill.",
        "You are running volumes where per-API-call economics and a dedicated integration team are the whole discussion.",
        "You want numbers in many countries, not India-first coverage.",
      ],
    },
    faqs: [
      {
        q: "Does OwnPBX have an API at all?",
        a: "Yes — a REST API and webhooks for extensions, numbers, call records and AI agent events, documented in the developer section of the docs. It is built for integrating a phone system with your CRM, not for building a communications product on top of.",
      },
      {
        q: "Can I use OwnPBX and a CPaaS together?",
        a: "Yes. Teams often keep a platform for application messaging and run the business phone system separately. Bring-your-own-carrier means the two do not fight over your numbers.",
      },
      {
        q: "Can the AI agent be triggered from my application?",
        a: "Outbound AI calls can be started through the API on your own caller ID, and the outcome comes back on a webhook.",
      },
    ],
    related: [
      "compare/knowlarity-alternative",
      "compare/callhippo-alternative",
      "compare/hindi-ai-voice-agent",
    ],
  },

  "compare/myoperator-alternative": {
    category: "Alternatives",
    title: "Looking for a MyOperator alternative?",
    lead:
      "OwnPBX is a full multi-tenant phone system — extensions, desk phones, IVR, queues, recording and reports — with AI voice agents built in, on your own carrier if you have one.",
    intro: [
      "MyOperator is in the call-management category that Indian SMBs most often start with: a number, a menu, calls pushed to the team's mobiles, and a dashboard of what happened. For a small sales team working from phones, that is frequently the right amount of product.",
      "OwnPBX is a heavier thing: a phone system with real extensions, with the AI agent inside it. This page sets out where that is worth it and where it is not. We make no claims about MyOperator's current features — their site is the place for those.",
    ],
    note: NO_CUSTOMERS,
    different: {
      h: "What you get that call-management tools usually do not",
      lead: "This is the trade: more system to set up, more that the system can do.",
      items: [
        {
          title: "Real extensions, not just call forwarding",
          desc:
            "Every person gets an extension they can register a desk phone, a laptop softphone or the browser phone against. Internal calls, transfers, pickup, voicemail per mailbox and call recording work the way they do on an office phone system.",
        },
        {
          title: "An AI agent that is part of the routing",
          desc:
            "Point a number or an IVR key at an AI agent and it answers on the first ring, in English, Hindi or Hinglish, and hands the caller to a person mid-call with context when it needs to.",
        },
        {
          title: "Bring your own carrier",
          desc:
            "Keep the numbers and calling contract you have and pay for software only, or buy calling from us. Either way the per-call cost is visible in the console rather than buried in a bundle.",
        },
        {
          title: "Multi-tenant, for branches and resellers",
          desc:
            "One platform holds many companies, each with its own extensions, numbers, users and reports — useful if you run several branches, and the basis of the reseller and white-label programmes.",
        },
      ],
    },
    table: {
      h: "Questions that decide this one",
      lead: "Ask both of us. The answers in the right-hand column are ours.",
      head: ["What to compare", "Ask any provider", "How OwnPBX works"],
      rows: [
        [
          "Desk phones",
          "Can I register an IP desk phone, or does everything go to mobiles?",
          "Desk phones, laptop softphones and a browser phone all register as extensions.",
        ],
        [
          "Mobile",
          "Is there a native mobile app for the team?",
          "Not yet. This is a real gap — the browser phone works in a phone's browser, but it is not an app.",
        ],
        [
          "AI voice agents",
          "Is the AI part of the phone system, and can it transfer a live call to a person?",
          "Yes to both, with the conversation context carried into the transfer.",
        ],
        [
          "Minutes",
          "Must I buy calling from you?",
          "No. Bring your own carrier, or buy calling from us.",
        ],
        [
          "Reporting",
          "Can I see an individual call's full journey, and its cost?",
          "Yes — per call, step by step, with cost, plus live wallboards.",
        ],
        [
          "Several branches",
          "Can one account separate branches with their own numbers and reports?",
          "Yes, the platform is multi-tenant.",
        ],
      ],
    },
    limits: {
      h: "What OwnPBX does not do",
      lead: "The first one on this list is the one that most often decides against us.",
      list: [
        "No mobile app. If your team lives on mobiles in the field, the browser phone is a workaround, not an answer.",
      ].concat(STANDARD_LIMITS.filter((l) => !l.startsWith("No mobile app"))),
    },
    betterFit: {
      h: "Where a lighter call-management tool is the better fit",
      lead: "Buying more system than you need is its own kind of expensive.",
      list: [
        "Your whole team works from mobile phones and a native app is non-negotiable.",
        "You want a number and a menu working this afternoon with nothing to configure.",
        "Nobody at your company wants to own a phone system, even a hosted one.",
        "You need WhatsApp in the same tool as the calls.",
      ],
    },
    faqs: [
      {
        q: "Do I need an IT person to run OwnPBX?",
        a: "No, but you do need someone willing to own the configuration — the person who decides what the menu says and who is in which queue. We set the account up with you and hand it over in that state.",
      },
      {
        q: "Can my team take calls without desk phones?",
        a: "Yes. The browser phone needs nothing installed, and softphones work on a laptop. The missing piece is a native mobile app.",
      },
      {
        q: "Is this too much product for five people?",
        a: "Sometimes, honestly. Five people who only need a number, a menu and call logs can be served by a lighter tool. The point at which OwnPBX pays off is usually queues, recording, several numbers, or wanting an AI agent to take first contact.",
      },
    ],
    related: [
      "compare/acefone-alternative",
      "compare/knowlarity-alternative",
      "compare/hindi-ai-voice-agent",
    ],
  },

  "compare/acefone-alternative": {
    category: "Alternatives",
    title: "Looking for a Servetel or Acefone alternative?",
    lead:
      "OwnPBX sells the phone system and the AI voice agents, and lets you decide where the calling comes from — your carrier or ours.",
    intro: [
      "Servetel and Acefone belong to the group of Indian providers that supply the numbers, the minutes and the software together. That is a genuinely convenient way to buy: one vendor, one bill, nothing to configure on the carrier side.",
      "OwnPBX unbundles it. You keep your carrier and pay us for software, or you buy calling from us as well — the platform does not care which. This page explains when that unbundling is worth the extra step, and when it is not. Nothing here is a claim about what Servetel or Acefone offer today.",
    ],
    note: NO_CUSTOMERS,
    different: {
      h: "What changes when the software and the minutes are separate",
      lead: "Mostly: where the leverage sits.",
      items: [
        {
          title: "You keep the carrier relationship",
          desc:
            "Rates are negotiated by you with your carrier, and your numbers stay in your name. If you ever leave the software, the numbers do not have to move with it — which also means the software has to keep earning its place.",
        },
        {
          title: "The bill separates into two things you can read",
          desc:
            "Software per extension on one side, calling on the other, and the cost of an individual call visible in the console next to that call. A bundle makes the question 'what did that support queue cost us last month' much harder to answer.",
        },
        {
          title: "AI voice agents included in the platform",
          desc:
            "The AI agent is a destination inside the phone system, speaking English, Hindi and Hinglish, able to pass a live caller to a human extension with the context of the conversation so far.",
        },
        {
          title: "Multi-tenant and white-label",
          desc:
            "If you resell, the platform runs many customers side by side with their own numbers and reports, and can carry your brand instead of ours.",
        },
      ],
    },
    table: {
      h: "Bundled or unbundled — the questions that settle it",
      lead: "Ask your shortlist the middle column. Ours are the answers on the right.",
      head: ["What to compare", "Ask any provider", "How OwnPBX works"],
      rows: [
        [
          "Who supplies the minutes",
          "Can I keep my own carrier, or must the calling come from you?",
          "Either. Bring your own SIP trunk, or buy calling from us.",
        ],
        [
          "Who owns the numbers",
          "If I leave, do the numbers stay with me?",
          "If they are yours, they stay yours — we never hold them. Numbers bought from us can be moved by the normal porting process.",
        ],
        [
          "Cost visibility",
          "Can I see what a single call cost?",
          "Yes, per call in the console, alongside the rest of its journey.",
        ],
        [
          "AI voice agents",
          "Is the AI part of the phone system or a separate product?",
          "Part of the platform; priced separately, because not everyone wants it.",
        ],
        [
          "Setup effort",
          "What do I have to configure before the first call?",
          "A trunk to point and IP addresses to allow, if you bring your own carrier. We do that part with you.",
        ],
        [
          "Reselling",
          "Can I run my own customers on it under my brand?",
          "Yes — multi-tenant, with reseller and white-label programmes.",
        ],
      ],
    },
    limits: {
      h: "What OwnPBX does not do",
      lead: "Worth reading before you move anything.",
      list: STANDARD_LIMITS.concat([
        "No international number inventory. We supply Indian city-code numbers; for other countries you bring your own carrier.",
      ]),
    },
    betterFit: {
      h: "Where buying everything from one vendor is the better choice",
      lead: "Unbundling has a cost, and it is not always worth paying.",
      list: [
        "You have no carrier relationship and no wish to acquire one.",
        "You want a single invoice and a single support number when a call fails, with nobody able to point at the other party.",
        "You need numbers in a hurry, in bulk, without a procurement conversation.",
        "Your volume is low enough that negotiating your own rates saves less than the effort costs.",
      ],
    },
    faqs: [
      {
        q: "What does bringing my own carrier actually involve?",
        a: "Your carrier gives you SIP trunk credentials or allows our IP addresses, and points your numbers at the trunk. We configure the trunk and the routing at our end. The slow part is almost always the carrier's internal process, not the configuration.",
      },
      {
        q: "Can I start with your calling and move to my own carrier later?",
        a: "Yes. The trunk is configuration, not a contract term — the platform supports both and you can change which one a number uses.",
      },
      {
        q: "Do you support multiple trunks at once?",
        a: "Yes, including routing different numbers or different tenants over different trunks.",
      },
    ],
    related: [
      "compare/bring-your-own-carrier-cloud-pbx",
      "compare/myoperator-alternative",
      "compare/knowlarity-alternative",
    ],
  },

  "compare/callhippo-alternative": {
    category: "Alternatives",
    title: "Looking for a CallHippo alternative?",
    lead:
      "OwnPBX is an India-first phone system with AI voice agents that speak Hindi and Hinglish, built to run on your own carrier.",
    intro: [
      "CallHippo belongs to the virtual-number category: numbers in a long list of countries, a dialler for the sales team, and software billed per user. For a team selling into many countries at once, breadth of number coverage is the whole product.",
      "OwnPBX is the opposite shape — deep on India rather than wide on geography. That is a real trade-off and it rules us out for some buyers. Nothing on this page describes CallHippo's current product; it compares the two approaches.",
    ],
    note: NO_CUSTOMERS,
    different: {
      h: "Deep on India instead of wide on countries",
      lead: "What that depth buys you.",
      items: [
        {
          title: "Voice agents built on Indian speech models",
          desc:
            "Indian names, numbers spoken the Indian way, and English and Hindi mixed inside a single sentence — handled as a caller actually speaks, across ten-plus Indian languages, rather than as an English model doing its best.",
        },
        {
          title: "A phone system, not only a dialler",
          desc:
            "IVR menus, queues with wait-time rules, business hours, voicemail per mailbox, recording, supervisor wallboards and multi-tenant separation. A dialler helps a sales team call out; this also answers the phone properly.",
        },
        {
          title: "City-code numbers Indian customers recognise",
          desc:
            "Mumbai, Delhi, Bengaluru, Chennai, Hyderabad, Pune, Kolkata and more, routed to extensions, an IVR, a queue or an AI agent. Or keep the numbers you already have.",
        },
        {
          title: "Per-call cost in the console",
          desc:
            "Each call's journey and its cost, visible as it happens, instead of a monthly summary you reconcile afterwards.",
        },
      ],
    },
    table: {
      h: "Which shape of product do you need?",
      lead: "Ask the middle column of anyone you shortlist.",
      head: ["What to compare", "Ask any provider", "How OwnPBX works"],
      rows: [
        [
          "Number coverage",
          "Which countries can you give me numbers in?",
          "Indian city-code numbers. For other countries you bring your own carrier.",
        ],
        [
          "Hindi and Hinglish",
          "Does the voice bot handle code-switching mid-sentence, and Indian names?",
          "Yes — that is what the agents are built on, across ten-plus Indian languages.",
        ],
        [
          "Inbound handling",
          "What happens to a call nobody answers — is there a real IVR and queue?",
          "IVR menus, queues, business hours, voicemail per mailbox and overflow to an AI agent.",
        ],
        [
          "Minutes",
          "Must the calling come from you?",
          "No. Bring your own carrier, or buy calling from us.",
        ],
        [
          "Per-user cost",
          "What happens to the bill when the team doubles?",
          "Priced per extension, with the per-call cost shown separately. The current tiers are on the pricing page.",
        ],
        [
          "Supervision",
          "Can a supervisor see and listen to calls as they happen?",
          "Live wallboards and live call monitoring, for human agents and AI agents alike.",
        ],
      ],
    },
    limits: {
      h: "What OwnPBX does not do",
      lead: "The first point is the one that decides most of these comparisons.",
      list: [
        "No international number inventory. If you need local numbers across dozens of countries, we are not the product.",
      ].concat(STANDARD_LIMITS),
    },
    betterFit: {
      h: "Where a global virtual-number provider is the better fit",
      lead: "Geography beats depth for plenty of teams.",
      list: [
        "You sell into many countries and need local numbers in each of them.",
        "Your team is distributed across time zones and calls out far more than it answers.",
        "A power dialler wired into your CRM is the thing that actually moves your numbers.",
        "You need a native mobile app for sellers on the road.",
      ],
    },
    faqs: [
      {
        q: "Can I use OwnPBX outside India?",
        a: "The platform itself is not India-only, and bringing your own carrier means you can use numbers from wherever your carrier supplies them. What we do not do is sell you numbers in other countries.",
      },
      {
        q: "Is there a dialler for outbound sales?",
        a: "Outbound calling works from the browser phone and softphones, and AI agents can place outbound calls on your own caller ID. There is no power or predictive dialler.",
      },
      {
        q: "Do AI agents work for outbound as well as inbound?",
        a: "Yes — follow-ups, reminders and outreach on your own caller ID, with the outcome, transcript and sentiment pushed to your systems on a webhook. Outbound calling to Indian numbers is subject to TRAI rules, which your use of it has to respect.",
      },
    ],
    related: [
      "compare/exotel-alternative",
      "compare/hindi-ai-voice-agent",
      "compare/knowlarity-alternative",
    ],
  },

  // --- category pages ------------------------------------------------------

  "compare/bring-your-own-carrier-cloud-pbx": {
    category: "Buying guide",
    title: "A cloud PBX where you bring your own carrier.",
    lead:
      "Keep the telecom contract, the rates and the numbers you already have, and pay for the phone system as software. Or buy calling from us — the platform supports both.",
    intro: [
      "Most Indian cloud phone systems sell the software and the minutes together. It is a tidy way to buy and a difficult way to leave: the numbers sit with the platform, the rate is whatever the bundle says, and the cost of a particular queue or campaign is hard to pull apart.",
      "Bring-your-own-carrier — BYOC — separates the two. You hold the carrier contract and the numbers; the phone system is software you point them at. This page explains what that involves, what OwnPBX does with it, and when the bundled model is simply the better deal.",
    ],
    note: NO_CUSTOMERS,
    different: {
      h: "What bring-your-own-carrier gets you",
      lead: "Four practical consequences, not a philosophy.",
      items: [
        {
          title: "Your rate is your rate",
          desc:
            "Whatever you negotiated with your carrier is what you pay for calling. Volume discounts you have earned stay with you instead of being absorbed into a per-extension bundle.",
        },
        {
          title: "Numbers you can leave with",
          desc:
            "Your numbers remain on your carrier account. Changing phone system means repointing a trunk, not porting your entire estate and reprinting everything that has a number on it.",
        },
        {
          title: "Two bills you can actually read",
          desc:
            "Software per extension from us, calling from your carrier, and each call's own cost in the console next to the call. Costing a support line stops being an exercise in interpreting a bundle.",
        },
        {
          title: "AI voice agents on top of it",
          desc:
            "The AI agent is a destination inside the same phone system, so an agent answering on your own trunk uses your own carrier's rate for the minutes, and can hand the caller to a human extension mid-call.",
        },
      ],
    },
    table: {
      h: "Bundled minutes or your own carrier",
      lead:
        "This table compares two ways of buying, not two companies. Both are legitimate; they suit different businesses.",
      head: ["What to compare", "Bundled with the software", "Bring your own carrier, on OwnPBX"],
      rows: [
        [
          "Who you buy minutes from",
          "The same vendor that sells you the software.",
          "Your carrier, at your negotiated rate — or us, if you prefer the convenience.",
        ],
        [
          "Where the numbers live",
          "With the platform.",
          "On your carrier account, in your name.",
        ],
        [
          "Switching cost later",
          "Numbers and minutes move together with the platform.",
          "Repoint a trunk. The numbers do not move.",
        ],
        [
          "Setup effort",
          "Lower — nothing to arrange on the carrier side.",
          "Higher — trunk credentials or IP allow-listing, and numbers pointed at the trunk. We configure our end with you.",
        ],
        [
          "Cost visibility",
          "One combined figure.",
          "Software and calling separate, with per-call cost in the console.",
        ],
        [
          "Who to call when a call fails",
          "One vendor, whatever the cause.",
          "Us for the platform, your carrier for the line. We will tell you which it is from the call's own trace.",
        ],
      ],
    },
    limits: {
      h: "What you should know before choosing BYOC",
      lead: "The honest friction.",
      list: [
        "Someone on your side has to raise a request with the carrier and see it through. That is usually the long pole, and it is outside our control.",
        "You hold two commercial relationships instead of one.",
        "If a call fails, the first question is whose side it failed on. The per-call trace answers that quickly, but it is still a question a bundle would never ask you.",
        "We supply Indian city-code numbers only; numbers elsewhere have to come from your own carrier.",
      ].concat(STANDARD_LIMITS.slice(0, 3)),
    },
    betterFit: {
      h: "When bundled minutes are the better deal",
      lead: "Plenty of the time, frankly.",
      list: [
        "You have no carrier relationship today and no appetite for setting one up.",
        "Your volume is low enough that your own negotiated rate would barely differ from a bundle.",
        "You want one invoice, one support line and nobody to point at anyone else.",
        "You need to be live this week and cannot wait on a carrier's process.",
      ],
    },
    faqs: [
      {
        q: "What exactly do I need from my carrier?",
        a: "Either SIP trunk credentials or an IP allow-list entry for our media servers, and your numbers pointed at that trunk. Carriers do this routinely; what varies is how long their process takes.",
      },
      {
        q: "Can different numbers use different trunks?",
        a: "Yes, including different trunks per tenant, which is how resellers and multi-branch businesses usually run it.",
      },
      {
        q: "Can I buy calling from OwnPBX instead?",
        a: "Yes. The pricing page lists both shapes — software only, and software with calling included. Which one a number uses is configuration, so you are not locked into the decision you make on day one.",
      },
      {
        q: "Does the AI voice agent work over my own trunk?",
        a: "Yes. The agent is a destination inside the phone system, so calls it answers or places run over whichever trunk that number uses.",
      },
    ],
    related: [
      "compare/acefone-alternative",
      "compare/knowlarity-alternative",
      "compare/hindi-ai-voice-agent",
    ],
  },

  "compare/hindi-ai-voice-agent": {
    category: "Buying guide",
    title: "A Hindi and Hinglish AI voice agent for business calls.",
    lead:
      "An AI agent that answers and places calls in English, Hindi and the Hinglish people actually speak — and that lives inside the phone system, so it can pass a caller to a person mid-call.",
    intro: [
      "Most voice bots sold in India are English-first models with Hindi added afterwards. They cope with a sentence that is entirely Hindi or entirely English, and come apart on the sentence that is half of each — which is how a great many Indian customers speak on the phone, especially about numbers, names and addresses.",
      "OwnPBX agents are built on Indian speech models, so code-switching inside a sentence is the normal case rather than the failure case. This page explains what the agent does, what it does not do, and when a human or a plain IVR is still the right answer.",
    ],
    note: NO_CUSTOMERS,
    different: {
      h: "What the agent does on a call",
      lead: "All of this is live today.",
      items: [
        {
          title: "Answers in the caller's language, and follows them when they switch",
          desc:
            "English, Hindi, natural Hinglish and ten-plus Indian languages. Grammar and tone are matched to the chosen voice, and the agent moves between languages mid-conversation as the caller does.",
        },
        {
          title: "Hands over to a human with context",
          desc:
            "Because the agent is a destination inside the phone system and not a service bolted on beside it, it can transfer a live caller to a real extension or a queue, carrying what has been said so far. Escalation rules decide when that happens.",
        },
        {
          title: "Answers from a knowledge base you control",
          desc:
            "Each agent has its own knowledge base and its own personality, and it logs the questions it could not answer so you can fix them rather than guess.",
        },
        {
          title: "Everything is recorded, transcribed and scored",
          desc:
            "Recording and transcription on every call, sentiment and intent scoring, live monitoring for supervisors, and a webhook to your CRM the moment the call ends.",
        },
        {
          title: "Outbound as well as inbound",
          desc:
            "Follow-ups, reminders and outreach placed on your own caller ID. Outbound calling to Indian numbers is subject to TRAI rules, and how you use it has to stay inside them.",
        },
        {
          title: "Handles interruption",
          desc:
            "Natural barge-in, so a caller who talks over the agent is heard instead of waiting for it to finish its sentence.",
        },
      ],
    },
    table: {
      h: "Judging any Indian-language voice agent",
      lead:
        "These are the questions that separate a demo that impresses from an agent that survives Tuesday afternoon. Ask them of us too.",
      head: ["What to test", "Ask any provider", "How OwnPBX works"],
      rows: [
        [
          "Code-switching",
          "Give it a sentence that is half Hindi and half English. Does it cope, or reset?",
          "Built for exactly that case, on Indian speech models.",
        ],
        [
          "Names and numbers",
          "Read out an Indian name and a ten-digit number quickly. What comes back?",
          "Indian names and spoken numbers are part of what the models are built on.",
        ],
        [
          "Transfer",
          "Can it hand me to a human mid-call, and does the human know what was said?",
          "Yes — a transfer to an extension or a queue, with the conversation context.",
        ],
        [
          "Interruption",
          "Talk over it. Does it stop and listen?",
          "Natural barge-in handling.",
        ],
        [
          "Where it sits",
          "Is the agent part of the phone system, or a separate service calls are forwarded to?",
          "Inside the phone system: a destination a number, an IVR key or a queue overflow can ring.",
        ],
        [
          "Afterwards",
          "What reaches my CRM when the call ends, and how soon?",
          "A webhook with outcome, transcript, sentiment and the call's cost, on completion.",
        ],
      ],
    },
    limits: {
      h: "What the agent does not do",
      lead: "Ask anyone selling you a voice agent for a list like this.",
      list: [
        "Voice calls and web chat only. No WhatsApp, no SMS campaigns.",
        "It is not a human. On a long, emotional or genuinely novel call the right behaviour is to transfer, and the escalation rules exist so that it does.",
        "No published accuracy figure. Accuracy depends on the line, the accent and the subject, and a single headline number would be marketing rather than information. Test it on your own calls.",
        "No self-service signup — we configure the first agent with you.",
      ],
    },
    betterFit: {
      h: "When a human or a plain IVR is the better answer",
      lead: "An agent pointed at the wrong calls makes a worse impression than no agent at all.",
      list: [
        "Negotiation, complaints and anything where the caller is already upset — route those to people.",
        "Regulated advice, or any conversation where a wrong answer carries legal weight.",
        "Very low call volume, where a menu and a voicemail box do the job and cost nothing to run.",
        "Calls that are mostly collecting one piece of information — a well-built IVR is faster for the caller and cheaper for you.",
      ],
    },
    faqs: [
      {
        q: "Can I hear one before I talk to sales?",
        a: "Yes. The AI demos page has agents for support, sales, appointments, collections and real estate that you can speak to in the browser right now. The companies in them are fictional; the product is the real one.",
      },
      {
        q: "Which languages are covered?",
        a: "English, Hindi and Hinglish, plus Tamil, Telugu, Bengali, Marathi, Gujarati, Kannada, Malayalam, Punjabi, Odia, Urdu and Assamese.",
      },
      {
        q: "Can the agent work on numbers I already own?",
        a: "Yes — bring your own carrier and point any of your numbers at an agent, the same way you would point it at an extension or a queue.",
      },
      {
        q: "What happens to the calls the agent cannot answer?",
        a: "It transfers to a human by your escalation rules, and logs the question it could not handle so you can add it to the knowledge base.",
      },
    ],
    related: [
      "compare/knowlarity-alternative",
      "compare/exotel-alternative",
      "compare/bring-your-own-carrier-cloud-pbx",
    ],
  },
};

// The /compare hub. Separate from the pages above because it is a list of them,
// not one of them.
export const compareIndex = {
  category: "Compare",
  title: "Compare OwnPBX",
  lead:
    "Honest comparisons: what OwnPBX does, what it does not, and where another kind of product would serve you better.",
  intro: [
    "Every page below is written the same way. We do not make claims about another company's current product, because we cannot keep those accurate — the comparison tables ask questions you can put to any provider, and answer them only for ourselves.",
    "Each page also has a section on where the other category is the better fit. Those are real. We would rather lose a deal early than lose it after you have moved your numbers.",
  ],
  groups: [
    {
      h: "Alternatives",
      slugs: [
        "compare/knowlarity-alternative",
        "compare/exotel-alternative",
        "compare/myoperator-alternative",
        "compare/acefone-alternative",
        "compare/callhippo-alternative",
      ],
    },
    {
      h: "Buying guides",
      slugs: [
        "compare/bring-your-own-carrier-cloud-pbx",
        "compare/hindi-ai-voice-agent",
      ],
    },
  ],
};

export const compareSlugs = Object.keys(compare);
